import React, { useState, useEffect, useCallback } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';

import { Container } from 'react-bootstrap';

import { useUserStore } from 'Store/userStore';
import { logout as TokenLogout } from 'Services/AuthService';
import GuardedComponent from 'HOC/GuardedComponent';
import AuthLink from 'HOC/AuthLink';
import EventBus from 'Utils/EventBus';
import getUser from 'Utils/userAuth';

import AppGuide from 'Components/UI/AppGuide';
import Header from 'Components/Core/Header';
import Footer from 'Components/Core/Footer';

import Home from 'Pages/Home';
import Apps from 'Pages/Apps';
import Login from 'Pages/Login';
import Support from 'Pages/Support';
import NotFound from 'Pages/NotFound';

import ProfilePasswordChange from 'Pages/Profile/PasswordChange';

import ResetPassword from 'Pages/Account/ResetPassword';
import ForgetPassword from 'Pages/Account/ForgetPassword';
import Activation from 'Pages/Account/Activation';

import AdminHome from 'Pages/Admin/Home';

import AdminApiKeysIndex from 'Pages/Admin/ApiKeys/Index';
import AdminNotificationsIndex from 'Pages/Admin/Notifications/Index';

import AdminUsersIndex from 'Pages/Admin/Users/Index';
import AdminUsersCreateOrEdit from 'Pages/Admin/Users/CreateOrEdit';

import AdminAppsIndex from 'Pages/Admin/Apps/Index';
import AdminAppsCreateOrEdit from 'Pages/Admin/Apps/CreateOrEdit';
import AdminAppsDelete from 'Pages/Admin/Apps/Delete';

import AdminAccessDelete from 'Pages/Admin/Accesses/User';

import AdminGroupsIndex from 'Pages/Admin/Groups/Index';
import AdminGroupsCreateOrEdit from 'Pages/Admin/Groups/CreateOrEdit';
import AdminGroupsDelete from 'Pages/Admin/Groups/Delete';

import AdminFonctionsIndex from 'Pages/Admin/Fonctions/Index';
import AdminFonctionsCreateOrEdit from 'Pages/Admin/Fonctions/CreateOrEdit';
import AdminFonctionsDelete from 'Pages/Admin/Fonctions/Delete';

import AdminProjetsIndex from 'Pages/Admin/Projets/Index';
import AdminProjetsCreateOrEdit from 'Pages/Admin/Projets/CreateOrEdit';
import AdminProjetsDelete from 'Pages/Admin/Projets/Delete';

import AdminDocumentationsIndex from 'Pages/Admin/Documentations/Index';
import AdminDocumentationsCreateOrEdit from 'Pages/Admin/Documentations/CreateOrEdit';
import AdminDocumentationsDelete from 'Pages/Admin/Documentations/Delete';

import AdminEventsIndex from 'Pages/Admin/Events/Index';
import AdminEventsCreateOrEdit from 'Pages/Admin/Events/CreateOrEdit';
import AdminEventsSubscribers from 'Pages/Admin/Events/Subscribers';
import AdminEventsDelete from 'Pages/Admin/Events/Delete';

import AdminHierachyGraph from 'Pages/Admin/Hierarchies/Graph';
import UserTreeView from 'Pages/Admin/HierarchiesUsers/UserTreeView';

import AdminDepartementsIndex from 'Pages/Admin/Departements/Index';
import AdminDepartementsCreateOrEdit from 'Pages/Admin/Departements/CreateOrEdit';
import AdminDepartementsDelete from 'Pages/Admin/Departements/Delete';

import AdminServicesIndex from 'Pages/Admin/Services/Index';
import AdminServicesCreateOrEdit from 'Pages/Admin/Services/CreateOrEdit';
import AdminServicesDelete from 'Pages/Admin/Services/Delete';

import TermsAdmin from 'Pages/Terms/TermsAdmin';
import TermsHistory from 'Pages/Terms/TermsHistory';

import TermsAcceptanceModal from 'Pages/Terms/TermsAcceptanceModal';
import { getAcceptanceStatus } from 'Services/TermsService';

import LoadingBackDropSpin from 'Components/Shared/LoadingBackDrop';
import { ToastContainer } from 'react-toastify';

import { readCookie, deleteCookie } from 'Utils/Helpers';
import isDev, { COOKIE_EXPIRATION_CHECKER, DOMAIN } from 'Utils/envLoader';

const App = () => {
    const user = useUserStore((state) => state.user);
    const setUser = useUserStore((state) => state.setUser);
    const clearUser = useUserStore((state) => state.clearUser);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const history = useHistory();

    const checkTermsStatus = async () => {
        const response = await getAcceptanceStatus();
        setTermsAccepted(response.data.hasAcceptedLatest);
    };

    useEffect(() => {
        const userDecoded = getUser();
        if (userDecoded) setUser(userDecoded);
    }, [setUser]);

    const logOut = useCallback(() => {
        clearUser();
        deleteCookie(COOKIE_EXPIRATION_CHECKER, `${isDev ? DOMAIN : '.' + DOMAIN}`);
        TokenLogout();
    }, [user]);

    useEffect(() => {
        if (
            history &&
            user &&
            user.MustChangePassword === 'True' &&
            window.location.pathname !== '/profile/password' &&
            window.location.pathname !== '/login' &&
            window.location.pathname !== '/password/forget' &&
            !window.location.pathname.includes('/password/reset/') &&
            !window.location.pathname.includes('/activation/') &&
            window.location.pathname !== '/not-found'
        ) {
            history.replace('/profile/password');
        }
    }, [user, history]);

    useEffect(() => {
        let timer;
        if (user) {
            // Function to check terms status and show modal if not accepted
            checkTermsStatus();

            // Set interval to check every second if the expiration cookie is still present
            timer = setInterval(() => {
                const exChecker = readCookie(COOKIE_EXPIRATION_CHECKER);
                if (!exChecker) logOut();
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [user]);

    useEffect(() => {
        EventBus.on('logout', () => {
            logOut();
        });

        return () => {
            EventBus.remove('logout');
        };
    }, [user, logOut]);

    EventBus.on('logout', () => {
        logOut();
    });

    return (
        <AppGuide>
            <div className="App">
                <LoadingBackDropSpin />
                {user && !termsAccepted && <TermsAcceptanceModal onAccepted={() => setTermsAccepted(true)} />}
                <header className="App-header">
                    <Header logOut={logOut} />
                </header>
                <main className="App-main">
                    <Container fluid>
                        <Switch>
                            <Route path="/login" component={Login} />
                            <Route path="/password/forget" component={ForgetPassword} />
                            <Route path="/password/reset/:token" component={ResetPassword} />
                            <Route path="/activation/:token" component={Activation} />

                            <GuardedComponent guarded path="/apps" component={Apps} />

                            <GuardedComponent guarded path="/profile/password" component={ProfilePasswordChange} />

                            <GuardedComponent guarded path="/support/:appId" component={Support} />

                            {/* Admin route - protect with your auth guard */}
                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/terms"
                                component={TermsAdmin}
                            />
                            {/* User route */}
                            <GuardedComponent guarded path="/my-terms-history" component={TermsHistory} />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/api-key/index"
                                component={AdminApiKeysIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/notification/index"
                                component={AdminNotificationsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/user/index"
                                component={AdminUsersIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/user/create"
                                component={AdminUsersCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/user/edit/:userId"
                                component={AdminUsersCreateOrEdit}
                            />

                            {/* <GuardedComponent guarded allowRoles={['SUPERADMIN']} path="/admin/user/delete/:userId" component={AdminUsersDelete} /> */}

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/access/user/:userId"
                                component={AdminAccessDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/app/index"
                                component={AdminAppsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/app/create"
                                component={AdminAppsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/app/edit/:appId"
                                component={AdminAppsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/app/delete/:appId"
                                component={AdminAppsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/group/index"
                                component={AdminGroupsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/group/create"
                                component={AdminGroupsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/group/edit/:groupId"
                                component={AdminGroupsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/group/delete/:groupId"
                                component={AdminGroupsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/fonction/index"
                                component={AdminFonctionsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/fonction/create"
                                component={AdminFonctionsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/fonction/edit/:fonctionId"
                                component={AdminFonctionsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/fonction/delete/:fonctionId"
                                component={AdminFonctionsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/projet/index"
                                component={AdminProjetsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/projet/create"
                                component={AdminProjetsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/projet/edit/:projetId"
                                component={AdminProjetsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/projet/delete/:projetId"
                                component={AdminProjetsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/documentation/index"
                                component={AdminDocumentationsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/documentation/create"
                                component={AdminDocumentationsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/documentation/edit/:documentationId"
                                component={AdminDocumentationsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/documentation/delete/:documentationId"
                                component={AdminDocumentationsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/event/index"
                                component={AdminEventsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/event/create"
                                component={AdminEventsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/event/edit/:eventId"
                                component={AdminEventsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/event/subscribers/:eventId"
                                component={AdminEventsSubscribers}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/event/delete/:eventId"
                                component={AdminEventsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/hierarchy/graph"
                                component={AdminHierachyGraph}
                            />
                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/hierarchy/users"
                                component={UserTreeView}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/departement/index"
                                component={AdminDepartementsIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/departement/create"
                                component={AdminDepartementsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/departement/edit/:departementId"
                                component={AdminDepartementsCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/departement/delete/:departementId"
                                component={AdminDepartementsDelete}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/service/index"
                                component={AdminServicesIndex}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/service/create"
                                component={AdminServicesCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/service/edit/:serviceId"
                                component={AdminServicesCreateOrEdit}
                            />

                            <GuardedComponent
                                guarded
                                allowRoles={['SUPERADMIN']}
                                path="/admin/service/delete/:serviceId"
                                component={AdminServicesDelete}
                            />

                            <GuardedComponent guarded allowRoles={['SUPERADMIN']} path="/admin" component={AdminHome} />

                            <GuardedComponent guarded path="/" component={Home} exact />
                            <Route path="/not-found" component={NotFound} />
                            <Redirect to="/not-found" />
                        </Switch>
                    </Container>
                </main>
                <footer className="App-footer">
                    <AuthLink logOut={logOut} />
                    <ToastContainer />
                    <Footer />
                </footer>
            </div>
        </AppGuide>
    );
};

export default App;
